import { useContext, useState, useRef, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';

import { makeGetDoc } from 'selectors/docs';
import { makeGetCurrentUserId } from 'selectors/users';

import { addDocMembers, removeDocMembers } from 'actions/docs';

import { docPermissions } from 'constants';

import { trackEvent } from 'services/events-tracker-service';
import { getDocPermissionLabel } from 'utils/user';
import { useCanViewOtherUsers } from 'shared-hooks/project';

import { RolesAndPermissionsContext } from 'context/RolesAndPermissionsContext';

import UserFaces from 'components/user-faces';
import UserPermissionSelector from 'components/user-permission-selector';
import AddUserPopout from 'components/add-user-popout';
import DateTime from 'components/DateTime';

import IconArrowDown from 'icons/icon-arrow-down';
import IconInvite from 'icons/icon-invite';

const docPermissionsCollection = docPermissions();

const EMPTY_ARRAY = [];

const DocumentUsers = ({ id, currentUserId, members, doc, editableAssignees = true, size = 'medium', maxUsersCount = 4, onPopoutShow, onPopoutHide }) => {
    const { t } = useTranslation(['doc', 'date']);
    const rolesAndPermissionsCtx = useContext(RolesAndPermissionsContext);
    const [selectedUser, setSelecteduser] = useState(null);
    const userPermissionCtrl = useRef(null);
    const canViewOtherUsers = useCanViewOtherUsers(doc?.project);

    const permission = useMemo(() => {
        if (doc?.author === currentUserId) {
            return 'edit';
        }

        const user = members.find(user => user.id === currentUserId);
        return user && user.permission;
    }, [doc, currentUserId, members]);

    const currentUserCanEdit = useMemo(() => {
        return permission === 'edit' && editableAssignees;
    }, [permission, editableAssignees]);

    const isInternal = useMemo(() => {
        return doc?.type === 'nifty';
    }, [doc]);

    const usersPermissions = useMemo(() => {
        if (!isInternal) {
            return EMPTY_ARRAY;
        }

        return members.map(user => ({
            id: user.id,
            subtitle: t(`permissions.${getDocPermissionLabel(user.permission)}`)
        }));
    }, [isInternal, members]);

    const handleUserClick = useCallback((userId, event) => {
        const targetUserPermission = members.find(user => user.id === userId)?.permission;
        if (userId === doc?.author && targetUserPermission === 'edit') {
            return;
        }

        setSelecteduser(userId);

        requestAnimationFrame(() => {
            userPermissionCtrl.current?.show(event);
        });
    }, [members, doc, userPermissionCtrl]);

    const handleUserPermissionSelectorHide = useCallback(() => {
        setSelecteduser(null);
    }, []);

    const handleUserPermissionChange = useCallback((userId, permission) => {
        addDocMembers(id, [{ id: userId, permission }]);
    }, [id]);

    const handleUserRemove = useCallback(userId => {
        removeDocMembers(id, [ userId ]);
    }, [id]);

    const handleUserInvite = useCallback(({ id: userId, permission }) => {
        addDocMembers(id, [{ id: userId, permission }]);
        trackEvent('DOC.USERS_ASSIGN', { permission });
    }, [id]);

    if (!doc || doc?.access_type === 'public') {
        return null;
    }

    if (!canViewOtherUsers) {
        return null;
    }

    return (
        <div className="users-list-component">
            {
                currentUserCanEdit &&
                    <>
                        <UserFaces
                            users={members.map(user => user.id)}
                            size={size}
                            maxUsersCount={maxUsersCount}
                            subtitles={usersPermissions}
                            subtitleIcon={<IconArrowDown size="8px" style={{ position: 'relative', top: '-1px' }} />}
                            additionals={members.map(user => {
                                const lastSeen = user.last_seen;
                                return {
                                    id: user.id,
                                    additional: lastSeen ? <Trans
                                        i18nKey="date:Viewed on"
                                        values={{
                                            date: new Date(lastSeen)
                                        }}
                                        components={{
                                            DateTime: <DateTime format="fullDateTime" />
                                        }}
                                    /> : null
                                }
                            })}
                            onSubtitleClick={handleUserClick}
                            onClick={handleUserClick}
                        />

                        {
                            selectedUser ? (
                                <UserPermissionSelector
                                    provideController={userPermissionCtrl}
                                    userId={selectedUser}
                                    permission={members.find(user => user.id === selectedUser).permission}
                                    permissions={docPermissionsCollection}
                                    onRemove={handleUserRemove}
                                    onSuccess={handleUserPermissionChange}
                                    onHide={handleUserPermissionSelectorHide}
                                />
                            ) : null
                        }
                    </>
            }

            {
                !currentUserCanEdit &&
                    <UserFaces
                        size={size}
                        users={members.map(user => user.id)}
                        subtitles={usersPermissions}
                        additionals={members.map(user => ({id: user.id, additional: <Trans
                            i18nKey="date:Viewed on"
                            values={{
                                date: user.last_seen
                            }}
                            components={{
                                DateTime: <DateTime format="fullDateTime" />
                            }}
                        />}))}
                    />
            }

            {
                currentUserCanEdit &&
                    <div className="add-user">
                        <AddUserPopout
                            title={ t('actions.Add user to Doc') }
                            projectId={doc?.project}
                            onUserSelect={handleUserInvite}
                            roles={rolesAndPermissionsCtx?.getRoles()}
                            emailMatchText={ t('invite.match') }
                            noMatchText={ t('invite.noMatch') }
                            excludedUsers={members.map(user => user.id)}
                            permissions={doc.access_type !== 'public' && docPermissionsCollection}
                            onShow={onPopoutShow}
                            onHide={onPopoutHide}
                        >
                            <button className={`btn btn-simple btn-${size} btn-square btn-icon btn-colored`}>
                                <IconInvite />
                            </button>
                        </AddUserPopout>
                    </div>
            }
        </div>
    );
}

const makeMapStateToProps = () => {
    const getCurrentUserId = makeGetCurrentUserId();
    const getDoc = makeGetDoc();

    return ({ docs, users }, ownProps) => {
        const doc = getDoc(docs.items, ownProps.id);

        return {
            currentUserId: getCurrentUserId(users),
            members: doc?.members || EMPTY_ARRAY,
            doc
        }
    };    
}

export default connect(makeMapStateToProps)(DocumentUsers);
